import { EMPTY_FORM_TOKEN_CHAIN } from '../constants';
import { UserState } from '../types/context';
import { dehydrateFormTokensMeta } from './dehydrateFormTokensMeta';
import { isFormTokensDbAvailable } from './formTokensStorage/isFormTokensDbAvailable';
import { pruneFormTokensToActiveChain } from './pruneFormTokensToActiveChain';
import { setLocalStorageItemSafely } from './setLocalStorageItemSafely';
import { getPersistableUserState } from './getPersistableUserState';

// Bulky token meta lives in IndexedDB (JS-1208); the blob keeps the chain with
// string references in place of meta so boot knows which records to load.
// Quota degradation ladder (both backends): full chain → active chain → no chain.
export const persistUserState = (
  localStorageKey: string,
  state: UserState
): void => {
  const idbAvailable = isFormTokensDbAvailable();
  const persistableState = getPersistableUserState(state);

  const fullState = idbAvailable
    ? {
        ...persistableState,
        formTokens: dehydrateFormTokensMeta(state.formTokens),
      }
    : persistableState;

  if (setLocalStorageItemSafely(localStorageKey, fullState)) {
    return;
  }

  const activeTokenId = new URLSearchParams(window.location.search).get(
    'token'
  );
  const activeChain = pruneFormTokensToActiveChain(
    state.formTokens,
    activeTokenId
  );
  const stateWithActiveChainOnly = {
    ...persistableState,
    formTokens: idbAvailable
      ? dehydrateFormTokensMeta(activeChain)
      : activeChain,
  };

  if (setLocalStorageItemSafely(localStorageKey, stateWithActiveChainOnly)) {
    console.warn(
      'localStorage quota exceeded - form tokens pruned to the active chain'
    );
    return;
  }

  const stateWithoutFormTokens = {
    ...persistableState,
    formTokens: EMPTY_FORM_TOKEN_CHAIN,
  };

  if (setLocalStorageItemSafely(localStorageKey, stateWithoutFormTokens)) {
    console.warn(
      'localStorage quota exceeded - user state persisted without form tokens'
    );
    return;
  }

  console.warn('localStorage quota exceeded - user state was not persisted');
};
